import { jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { useTheme } from '../../theme';
const STYLE_ID = 'loci-skeleton-keyframes';
export const resolveRadius = (radius, theme) => {
    if (typeof radius === 'string' && radius in theme.borderRadius) {
        return theme.borderRadius[radius];
    }
    return radius;
};
// Inject keyframes once at module load time
const ensureKeyframesInjected = (() => {
    if (typeof document === 'undefined')
        return;
    if (document.getElementById(STYLE_ID))
        return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
    @keyframes skeleton-shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }

    @keyframes skeleton-pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(0.95); }
    }

    .fs-skeleton-shimmer::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%);
      animation: skeleton-shimmer 1500ms ease-in-out infinite;
      will-change: transform;
    }
  `;
    document.head.appendChild(style);
})();
const useReducedMotion = () => {
    const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(() => typeof window !== 'undefined'
        ? (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
        : false);
    React.useEffect(() => {
        const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        if (!mq)
            return;
        const handler = (e) => setPrefersReducedMotion(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);
    return prefersReducedMotion;
};
export const Skeleton = ({ animation = 'shimmer', radius = 'md', ariaLabel = 'Loading', className, style, ...rest }) => {
    const theme = useTheme();
    const prefersReducedMotion = useReducedMotion();
    const baseColor = theme.mode === 'dark' ? theme.neutral[700] : theme.neutral[200];
    const highlightColor = theme.mode === 'dark' ? theme.neutral[600] : theme.neutral[100];
    const resolvedRadius = resolveRadius(radius, theme) ?? theme.borderRadius.md;
    const animationStyle = useMemo(() => {
        if (animation === 'none' || prefersReducedMotion) {
            return {
                backgroundColor: baseColor,
            };
        }
        if (animation === 'pulse') {
            return {
                backgroundColor: baseColor,
                animation: 'skeleton-pulse 1500ms ease-in-out infinite',
            };
        }
        return {
            backgroundColor: baseColor,
            position: 'relative',
            overflow: 'hidden',
        };
    }, [animation, baseColor, highlightColor, prefersReducedMotion]);
    const combinedClassName = [
        'fs-skeleton',
        animation === 'shimmer' && !prefersReducedMotion ? 'fs-skeleton-shimmer' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');
    return (_jsx("div", { role: "status", "aria-label": ariaLabel, className: combinedClassName, style: {
            borderRadius: resolvedRadius,
            ...animationStyle,
            ...style,
        }, ...rest }));
};
